import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { completePasswordReset, verifyPasswordResetCode } from '../api/auth';
import { ApiError } from '../api/client';
import { Button, Input } from '../components/ui';
import styles from './LoginPage.module.scss';

export function ResetAdminPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Emailed links carry the challenge in the query string; the in-app
  // forgot-password hop passes it (plus the masked email) in history state.
  const routeState = location.state as { challengeToken?: unknown; maskedEmail?: unknown } | null;
  const stateChallengeToken = routeState?.challengeToken;
  const challengeToken =
    searchParams.get('challenge') ||
    (typeof stateChallengeToken === 'string' ? stateChallengeToken : '');
  const maskedEmail = routeState?.maskedEmail;
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const verified = await verifyPasswordResetCode({
        challenge_token: challengeToken,
        code: code.trim(),
      });
      await completePasswordReset({
        reset_token: verified.reset_token,
        new_password: password,
      });
    },
  });

  const isMissingChallenge = !challengeToken;
  const errorMessage =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error instanceof Error
        ? t('auth.resetPasswordError')
        : null;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    if (!mutation.isPending) mutation.mutate();
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.brandLabel}>White&amp;Flex</span>
          <h1 className={styles.title}>{t('auth.resetPasswordTitle')}</h1>
          <p className={styles.subtitle}>
            {typeof maskedEmail === 'string' && maskedEmail
              ? t('auth.resetPasswordCodeSent', { email: maskedEmail })
              : t('auth.resetPasswordHint')}
          </p>
        </div>

        {isMissingChallenge ? (
          <div className={styles.alert} role="alert">
            {t('auth.resetPasswordInvalidLink')}
          </div>
        ) : null}
        {mutation.isError && !mismatch ? (
          <div className={styles.alert} role="alert">
            {errorMessage}
          </div>
        ) : null}
        {mismatch ? (
          <div className={styles.alert} role="alert">
            {t('roles.passwordMismatch')}
          </div>
        ) : null}

        {mutation.isSuccess ? (
          <>
            <p className={styles.subtitle} role="status">
              {t('auth.resetPasswordSuccess')}
            </p>
            <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/login')}>
              {t('auth.backToSignIn')}
            </Button>
          </>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.fields}>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{4}"
                maxLength={4}
                label={t('auth.resetCode')}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
                disabled={isMissingChallenge || mutation.isPending}
              />
              <Input
                id="new-password"
                type="password"
                minLength={8}
                label={t('auth.newPassword')}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
                disabled={isMissingChallenge || mutation.isPending}
              />
              <Input
                id="confirm-password"
                type="password"
                minLength={8}
                label={t('auth.confirmNewPassword')}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                required
                disabled={isMissingChallenge || mutation.isPending}
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isMissingChallenge || mutation.isPending}
            >
              {mutation.isPending ? t('auth.resettingPassword') : t('auth.resetPasswordSubmit')}
            </Button>
            <Link to="/forgot-password" className={styles.textLink}>
              {t('auth.requestNewCode')}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
